const CUSTOMER_PORTAL_SHADOW_PATH = '/api/customer/v1/portal';
const CUSTOMER_PORTAL_TIMEOUT_MS = 12000;

export class CustomerPortalShadowError extends Error {
  constructor(message, { code = 'CUSTOMER_PORTAL_ERROR', status = 0, requestId = '' } = {}) {
    super(message);
    this.name = 'CustomerPortalShadowError';
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}

export async function loadCustomerPortalShadow(user) {
  if (!user || typeof user.getIdToken !== 'function') {
    throw new CustomerPortalShadowError('Sign in to check customer portal readiness.', {
      code: 'UNAUTHENTICATED',
    });
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CUSTOMER_PORTAL_TIMEOUT_MS);
  try {
    const idToken = await user.getIdToken(false);
    const response = await fetch(CUSTOMER_PORTAL_SHADOW_PATH, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok && ['OPERATION_HELD', 'CONTRACT_NOT_READY'].includes(payload?.code)) {
      return {
        state: 'CONTRACT_HELD',
        gateId: payload.gateId || 'W0-ID-01',
        safeMessage: payload.safeMessage || 'Customer portal access is not enabled yet.',
        requestId: payload.requestId || response.headers.get('x-request-id') || '',
      };
    }
    if (!response.ok) {
      throw new CustomerPortalShadowError(
        payload?.safeMessage || 'Customer portal readiness is temporarily unavailable.',
        {
          code: payload?.code || 'CUSTOMER_PORTAL_ERROR',
          status: response.status,
          requestId: payload?.requestId || response.headers.get('x-request-id') || '',
        }
      );
    }

    throw new CustomerPortalShadowError('No W2 customer portal success contract has been accepted.', {
      code: 'SUCCESS_CONTRACT_DEFERRED',
      status: response.status,
      requestId: response.headers.get('x-request-id') || '',
    });
  } catch (error) {
    if (error instanceof CustomerPortalShadowError) throw error;
    if (error?.name === 'AbortError') {
      throw new CustomerPortalShadowError('Customer portal readiness timed out.', { code: 'TIMEOUT' });
    }
    throw new CustomerPortalShadowError('Customer portal readiness is temporarily unavailable.');
  } finally {
    clearTimeout(timeout);
  }
}

export { CUSTOMER_PORTAL_SHADOW_PATH };
